/** Blocs Hero, bandeau chiffres clés et CTA final. */
import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

/** Badge note Google — donnée dynamique (proxy backend, cache serveur), masqué si indisponible. */
function GoogleRatingBadge() {
  const [gr, setGr] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/google-reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((x) => { if (alive && x && x.rating != null) setGr(x); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!gr) return null;
  const ratingFr = gr.rating.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <motion.a initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              href="#avis-google" data-testid="hero-google-rating-badge"
              className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-lin/30 bg-black/25 backdrop-blur-sm text-lin text-xs tracking-wide hover:border-or hover:text-or transition-colors">
      <Star size={13} className="fill-or text-or" />
      <span><strong>{ratingFr}</strong> sur Google{gr.user_rating_count != null ? ` · ${gr.user_rating_count} avis` : ""}</span>
    </motion.a>
  );
}

/** Film sombre du Hero — dégradé dont l'intensité est réglable (film_enabled / film_opacity, défaut 40 %). */
function HeroOverlay({ d }) {
  if (d.film_enabled === false) return null;
  const o = Math.min(Math.max((d.film_opacity ?? 40) / 100, 0), 0.9);
  return (
    <div className="absolute inset-0"
         style={{ background: `linear-gradient(to bottom, rgba(0,0,0,${Math.min(o * 1.25, 0.9)}), rgba(0,0,0,${o * 0.625}), rgba(0,0,0,${Math.min(o * 1.5, 0.9)}))` }} />
  );
}

export function HeroBlock({ d, t, abs, isFirstHero }) {
  // SEO : un seul H1 par page — seul le premier Hero rend un <h1>, les suivants un <h2>.
  const HeroTitle = isFirstHero ? "h1" : "h2";
  const video = d.video_url_desktop || d.video_url;
  const videoMobile = d.video_url_mobile || video;
  const poster = d.poster_url ? abs(d.poster_url) : (d.image_url ? abs(d.image_url) : null);
  const fallback = d.image_url ? abs(d.image_url) : null;
  const align = d.align || "center";
  const hasMedia = !!(video || d.image_url);
  const heightClass = !hasMedia ? "min-h-[280px]" : (d.fullscreen === false ? "min-h-[500px]" : "min-h-[92vh]");
  const alignClass = align === "left" ? "text-left items-start"
    : align === "right" ? "text-right items-end"
    : "text-center items-center";
  const eyebrow = d.eyebrow ? t(d.eyebrow) : null;
  const baseline = d.baseline ? t(d.baseline) : null;
  const ctas = Array.isArray(d.ctas) && d.ctas.length > 0
    ? d.ctas.filter((c) => c.label && c.link)
    : (d.cta_label && d.cta_link ? [{ label: d.cta_label, link: d.cta_link }] : []);
  // Si un CTA pointe déjà vers la même ancre que le scroll_hint,
  // on masque la flèche pour éviter le doublon (ex: "Découvrir la Briouse" + "Découvrir le Domaine ↓").
  const scrollHintDup = d.scroll_hint && ctas.some((c) => c.link === (d.scroll_hint_link || "#intro"));
  const showScrollHint = d.scroll_hint && !scrollHintDup;
  const titleStyle = d.title_style || "editorial";
  const heroCtas = ctas.length > 0 && (
    <div className="bg-lin border-b border-pierre/60 py-6 md:py-8" data-testid="hero-ctas-below">
      <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row flex-wrap items-center gap-3 md:gap-4 justify-center">
        {ctas.map((c, i) => (
          <a key={`hcta-${i}`} href={c.link}
             data-testid={`hero-cta-${i}`}
             className={`w-full sm:w-auto text-center whitespace-nowrap ${
               i === 0
                 ? "inline-block px-6 md:px-8 py-3 bg-or text-domaine text-xs uppercase tracking-[0.25em] hover:bg-domaine hover:text-lin transition-colors"
                 : "inline-block px-6 md:px-8 py-3 border border-domaine text-domaine text-xs uppercase tracking-[0.25em] hover:bg-domaine hover:text-lin transition-colors"
             }`}>
            {t(c.label)}
          </a>
        ))}
      </div>
    </div>
  );
  if (titleStyle === "editorial") {
    // Titre hors photo : la photo reste pure, le H1 s'affiche dessous (lisibilité garantie).
    return (
      <>
        {hasMedia && (
          <section className="relative min-h-[42vh] md:min-h-[54vh] bg-domaine overflow-hidden -mx-6 sm:-mx-0"
                   data-testid="hero-editorial-media">
            {video ? (
              <>
                <video autoPlay muted loop playsInline poster={poster || undefined}
                       className="absolute inset-0 w-full h-full object-cover hidden md:block"
                       data-testid="hero-video-desktop">
                  <source src={abs(video)} type="video/mp4" />
                </video>
                <video autoPlay muted loop playsInline poster={poster || undefined}
                       className="absolute inset-0 w-full h-full object-cover md:hidden"
                       data-testid="hero-video-mobile">
                  <source src={abs(videoMobile)} type="video/mp4" />
                </video>
              </>
            ) : (
              <div className="absolute inset-0 bg-cover bg-center"
                   style={{ backgroundImage: `url(${fallback})` }} />
            )}
          </section>
        )}
        <div className={`bg-lin text-center px-6 pt-12 md:pt-14 pb-10 md:pb-12 -mx-6 sm:-mx-0 ${ctas.length > 0 ? "" : "border-b border-pierre/60"}`}
             data-testid="hero-editorial-banner">
          {eyebrow && <p className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-or mb-4">{eyebrow}</p>}
          <HeroTitle className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.12] text-domaine max-w-4xl mx-auto">{t(d.title)}</HeroTitle>
          {baseline && <p className="mt-5 text-sm sm:text-base uppercase tracking-[0.28em] text-or">{baseline}</p>}
          {d.subtitle && <p className={`text-base sm:text-lg text-ardoise/80 max-w-2xl mx-auto ${baseline ? "mt-3" : "mt-5"}`}>{t(d.subtitle)}</p>}
        </div>
        {heroCtas}
      </>
    );
  }
  if (titleStyle === "immersive") {
    // Mise en scène cinématique : titre, baseline et CTAs SUR la photo, entrée séquencée.
    return (
      <section className="relative left-1/2 -translate-x-1/2 w-screen min-h-[82vh] md:min-h-[88vh] flex items-center justify-center bg-domaine overflow-hidden"
               data-testid="hero-immersive">
        {video ? (
          <>
            <video autoPlay muted loop playsInline poster={poster || undefined}
                   className="absolute inset-0 w-full h-full object-cover hidden md:block" data-testid="hero-video-desktop">
              <source src={abs(video)} type="video/mp4" />
            </video>
            <video autoPlay muted loop playsInline poster={poster || undefined}
                   className="absolute inset-0 w-full h-full object-cover md:hidden" data-testid="hero-video-mobile">
              <source src={abs(videoMobile)} type="video/mp4" />
            </video>
          </>
        ) : fallback ? (
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${fallback})` }} />
        ) : null}
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.28) 45%, rgba(0,0,0,0.55))" }} />
        <motion.div initial="hidden" animate="visible"
                    variants={{ visible: { transition: { staggerChildren: 0.14 } } }}
                    className="relative z-10 px-6 pt-24 pb-28 max-w-5xl w-full flex flex-col items-center text-center">
          {eyebrow && (
            <motion.p variants={fadeUp} className="text-[11px] md:text-xs uppercase tracking-[0.35em] text-or mb-6">
              {eyebrow}
            </motion.p>
          )}
          <motion.div variants={fadeUp}>
            <HeroTitle className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.08]"
                       style={{ textShadow: "0 2px 18px rgba(0,0,0,0.6)" }}>
              {t(d.title)}
            </HeroTitle>
          </motion.div>
          {baseline && (
            <motion.p variants={fadeUp} className="mt-7 text-xs sm:text-sm uppercase tracking-[0.3em] text-or"
                      style={{ textShadow: "0 1px 10px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.7)" }}>
              {baseline}
            </motion.p>
          )}
          {d.subtitle && (
            <motion.p variants={fadeUp} className="mt-5 text-base sm:text-lg text-lin/90 max-w-2xl leading-relaxed"
                      style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
              {t(d.subtitle)}
            </motion.p>
          )}
          {ctas.length > 0 && (
            <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center gap-3 md:gap-4">
              {ctas.map((c, i) => (
                <a key={`icta-${i}`} href={c.link} data-testid={`hero-immersive-cta-${i}`}
                   className={`w-full sm:w-auto text-center whitespace-nowrap px-8 py-3.5 text-xs uppercase tracking-[0.25em] transition-colors ${
                     i === 0
                       ? "bg-or text-domaine hover:bg-lin"
                       : "border border-lin/70 text-lin hover:bg-lin hover:text-domaine"}`}>
                  {t(c.label)}
                </a>
              ))}
            </motion.div>
          )}
          {d.show_google_rating !== false && <GoogleRatingBadge />}
        </motion.div>
        {showScrollHint && (
          <a href={d.scroll_hint_link || "#intro"} data-testid="hero-scroll-hint"
             className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-lin/80 hover:text-or transition-colors">
            <span className="text-[10px] uppercase tracking-[0.3em]">{t(d.scroll_hint)}</span>
            <motion.span aria-hidden="true" animate={{ scaleY: [0.25, 1, 0.25] }}
                         transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                         className="block w-px h-8 bg-or origin-top" />
          </a>
        )}
      </section>
    );
  }
  return (
    <>
      <section className={`relative ${heightClass} flex items-center justify-center bg-domaine overflow-hidden -mx-6 sm:-mx-0`}>
        {video ? (
          <>
            <video
              autoPlay muted loop playsInline
              poster={poster || undefined}
              className="absolute inset-0 w-full h-full object-cover hidden md:block"
              data-testid="hero-video-desktop"
            >
              <source src={abs(video)} type="video/mp4" />
            </video>
            <video
              autoPlay muted loop playsInline
              poster={poster || undefined}
              className="absolute inset-0 w-full h-full object-cover md:hidden"
              data-testid="hero-video-mobile"
            >
              <source src={abs(videoMobile)} type="video/mp4" />
            </video>
          </>
        ) : fallback ? (
          <div className="absolute inset-0 bg-cover bg-center"
               style={{ backgroundImage: `url(${fallback})` }} />
        ) : null}
        <HeroOverlay d={d} />
        <div className={`relative z-10 text-lin px-8 py-16 max-w-4xl w-full flex flex-col ${alignClass}`}>
          {eyebrow && <div style={{ textShadow: "0 2px 12px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9)" }} className="text-[11px] md:text-xs uppercase tracking-[0.4em] text-or mb-4">{eyebrow}</div>}
          <HeroTitle style={{ color: "inherit", textShadow: "0 2px 16px rgba(0,0,0,0.75), 0 1px 3px rgba(0,0,0,0.6)" }} className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-tight">{t(d.title)}</HeroTitle>
          {(baseline || d.subtitle) && (
            <div className={`mt-6 inline-block bg-black/45 backdrop-blur-sm px-6 md:px-8 py-4 md:py-5 border border-white/10 ${align === "center" ? "mx-auto" : ""}`}
                 data-testid="hero-baseline-panel">
              {baseline && <p className="text-sm sm:text-base uppercase tracking-[0.28em] text-or/95">{baseline}</p>}
              {d.subtitle && <p className={`text-base sm:text-lg text-lin/95 max-w-2xl ${baseline ? "mt-3" : ""}`}>{t(d.subtitle)}</p>}
            </div>
          )}
          {showScrollHint && (
            <a href={d.scroll_hint_link || "#intro"}
               className="absolute bottom-8 left-1/2 -translate-x-1/2 text-lin/80 text-[10px] uppercase tracking-[0.3em] hover:text-or transition-colors">
              {t(d.scroll_hint)} ↓
            </a>
          )}
        </div>
      </section>
      {ctas.length > 0 && heroCtas}
    </>
  );
}

export function StatsBandBlock({ d, t }) {
  const stats = (d.stats || []).filter((s) => s.value);
  return (
    <section className="py-14 md:py-20 border-y border-pierre/60 bg-sable/20">
      {d.title && <h2 className="text-2xl md:text-3xl font-serif text-domaine text-center mb-2">{t(d.title)}</h2>}
      {d.subtitle && <p className="text-center text-ardoise/70 mb-10">{t(d.subtitle)}</p>}
      <div className={`grid grid-cols-2 md:grid-cols-${Math.min(stats.length, 5)} gap-8 max-w-5xl mx-auto px-6`}>
        {stats.map((s, i) => (
          <div key={`stat-${i}`} className="text-center">
            <div className="text-4xl md:text-5xl font-serif text-domaine">{t(String(s.value))}</div>
            <div className="text-[11px] uppercase tracking-widest text-or mt-2">{t(s.label)}</div>
            {s.description && <p className="text-xs text-ardoise/60 mt-1">{t(s.description)}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export function CtaFinalBlock({ d, t, abs }) {
  const ctas = (d.ctas || []).filter((c) => c.label && c.link);
  return (
    <section className="relative min-h-[520px] flex items-center justify-center text-center overflow-hidden -mx-6 sm:-mx-0">
      {d.image_url && (
        <div className="absolute inset-0 bg-cover bg-center"
             style={{ backgroundImage: `url(${abs(d.image_url)})` }} />
      )}
      {(d.film_enabled ?? true) && (
        <div className="absolute inset-0"
             style={{ backgroundColor: `rgba(0,0,0,${Math.min((d.film_opacity ?? 55) / 100, 0.9)})` }} />
      )}
      <div className="relative z-10 text-lin px-8 py-20 max-w-3xl">
        {d.eyebrow && <div className="text-xs uppercase tracking-[0.4em] text-or mb-4">{t(d.eyebrow)}</div>}
        {d.title && <h2 style={{ color: "inherit" }} className="text-3xl md:text-5xl font-serif">{t(d.title)}</h2>}
        {d.subtitle && <p className="mt-4 text-base md:text-lg opacity-90 max-w-2xl mx-auto">{t(d.subtitle)}</p>}
        {ctas.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            {ctas.map((c, i) => (
              <a key={`fcta-${i}`} href={c.link}
                 className={i === 0
                   ? "inline-block px-6 py-3 bg-or text-domaine text-xs uppercase tracking-[0.25em] hover:bg-lin transition-colors"
                   : "inline-block px-6 py-3 border border-lin text-lin text-xs uppercase tracking-[0.25em] hover:bg-lin hover:text-domaine transition-colors"}>
                {t(c.label)}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
